import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Dispatch, RootState } from '../../store/store';
import Modal from '../Modal/Modal';
import ControllerSearchSelect from '../ControllerSearchSelect/ControllerSearchSelect';

type OwnProps = {
  isVisible: boolean;
  onClose: () => void;
  controllerSetupId: string | null;
};

type ComponentProps = OwnProps & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ApplyControllerSetupModal: React.FC<ComponentProps> = ({
  isVisible,
  onClose,
  controllerSetupId,
  controllers,
  getControllers,
  applyControllerSetup,
}) => {
  const [selectedControllerIds, setSelectedControllerIds] = useState<string[]>([]);
  const [overwriteChannelNames, setOverwriteChannelNames] = useState(false);
  const [overwriteCurrentScaling, setOverwriteCurrentScaling] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (isVisible) {
      getControllers();
    }
  }, [isVisible]);

  const resetAndClose = () => {
    setSelectedControllerIds([]);
    setOverwriteChannelNames(false);
    setOverwriteCurrentScaling(false);
    onClose();
  };

  const onApplyHandler = async () => {
    if (!controllerSetupId || !selectedControllerIds.length) return;

    setIsApplying(true);
    const results = await applyControllerSetup({
      id: controllerSetupId,
      payload: {
        controllerIds: selectedControllerIds,
        overwriteChannelNames,
        overwriteCurrentScaling,
      },
    });
    setIsApplying(false);

    if (!results) return;

    const succeeded = results.filter((item) => item.success).length;
    const failed = results.length - succeeded;

    if (succeeded) {
      toast.success(`Applied to ${succeeded} controller${succeeded > 1 ? 's' : ''}`);
    }
    if (failed) {
      toast.error(`Failed to apply to ${failed} controller${failed > 1 ? 's' : ''}`);
    }

    resetAndClose();
  };

  return (
    <Modal
      isVisible={isVisible}
      modalTitle="Apply Controller Setup"
      onClose={resetAndClose}
      onAccept={onApplyHandler}
      acceptBtnText={isApplying ? 'Applying...' : 'Apply'}
      acceptBtnDisabled={isApplying || !selectedControllerIds.length}
      cancelBtnDisabled={isApplying}
    >
      <div className="flex w-full flex-col gap-4 text-left">
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">Select controller(s)</p>
          <ControllerSearchSelect
            controllers={controllers}
            selectedIds={selectedControllerIds}
            onChange={setSelectedControllerIds}
            multiple
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2.5">
          <input
            type="checkbox"
            checked={overwriteChannelNames}
            onChange={(event) => setOverwriteChannelNames(event.target.checked)}
          />
          Overwrite channel names
        </label>
        <label className="flex cursor-pointer items-center gap-2.5">
          <input
            type="checkbox"
            checked={overwriteCurrentScaling}
            onChange={(event) => setOverwriteCurrentScaling(event.target.checked)}
          />
          Overwrite current scaling settings
        </label>
      </div>
    </Modal>
  );
};

const mapState = (state: RootState) => ({
  controllers: state.controllers.controllers_list,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllers: dispatch.controllers.getControllers,
  applyControllerSetup: dispatch.controller_setups.applyControllerSetup,
});

export default connect(mapState, mapDispatch)(ApplyControllerSetupModal);
